/**
 * DisbursementQuotesCard — BM-H5023 (3 Nov 2026).
 *
 * Vanessa: "i need a disbursement quote to be noted on matters so they flag
 * up with reminders if no MOA received e.g. barristers".
 *
 * Under the money bar. Anyone working the matter records a quote (who
 * quoted, what for, amount, VAT, date). Until accounts links money on
 * account received for it, it shows "Awaiting MOA" and cannot be paid
 * (Vanessa chose a hard block). Accounts and directors link receipts here.
 * Backed by /api/matters/{ref}/disbursement-quotes.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { getUserRole, ROLES } from "../config/roleConfig";
import { AlertTriangle, CheckCircle2, Link2, Loader2, Plus, Receipt, X } from "lucide-react";

const VAT_OPTIONS = [
  { value: "plus_vat", label: "Plus VAT" },
  { value: "inc_vat", label: "Includes VAT" },
  { value: "no_vat", label: "No VAT" },
];

function fmt(n) {
  if (n == null || isNaN(n)) return "£0.00";
  return "£" + Number(n).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

const inputCls = "bg-slate-800 border border-slate-600 rounded px-2 py-1 text-white outline-none focus:border-indigo-400";

function signedInEmail() {
  try {
    return sessionStorage.getItem("bonsai_user_email") || localStorage.getItem("bonsai_user_email")
      || (JSON.parse(localStorage.getItem("user") || "{}").email || "");
  } catch { return ""; }
}

// Shows the link button only; the server refuses anyone else (403).
export function canLinkReceipts(role) {
  return role === ROLES.ACCOUNTS || role === ROLES.DIRECTOR;
}

export default function DisbursementQuotesCard({ matterRef }) {
  const enc = encodeURIComponent(matterRef);
  const [quotes, setQuotes] = useState(null);
  const [loadErr, setLoadErr] = useState("");
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ supplier: "", description: "", amount: "", vat_treatment: "", quoted_on: todayIso() });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [linking, setLinking] = useState(null); // quote id
  const [receipts, setReceipts] = useState(null);
  const [withdrawing, setWithdrawing] = useState(null);
  const [reason, setReason] = useState("");
  const linker = canLinkReceipts(getUserRole(signedInEmail()));

  const load = useCallback(async () => {
    setLoadErr("");
    try {
      const res = await axios.get(`${API}/matters/${enc}/disbursement-quotes`);
      setQuotes(res.data.quotes || []);
    } catch (ex) {
      setLoadErr(errorMessage(ex, "Could not load disbursement quotes"));
    }
  }, [enc]);

  useEffect(() => { load(); }, [load]);

  const act = async (fn, fallback) => {
    setSaving(true);
    setErr("");
    try {
      const res = await fn();
      setQuotes(res.data.quotes || []);
      return true;
    } catch (ex) {
      setErr(errorMessage(ex, fallback));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    const val = parseFloat(String(form.amount).replace(/[£,]/g, ""));
    if (!form.supplier.trim()) return setErr("Say who quoted, for example counsel's name");
    if (!form.description.trim()) return setErr("Say what the quote is for");
    if (isNaN(val) || val <= 0) return setErr("Enter the quoted amount");
    if (!form.vat_treatment) return setErr("Say whether the quote is plus VAT, includes VAT, or has no VAT");
    const ok = await act(() => axios.post(`${API}/matters/${enc}/disbursement-quotes`, {
      supplier: form.supplier.trim(), description: form.description.trim(), amount: val,
      vat_treatment: form.vat_treatment, quoted_on: form.quoted_on,
    }), "Could not save the disbursement quote");
    if (ok) setAdding(false);
  };

  const openLink = async (quoteId) => {
    setLinking(quoteId);
    setReceipts(null);
    setErr("");
    try {
      const res = await axios.get(`${API}/matters/${enc}/disbursement-quotes/receipts`);
      setReceipts(res.data.receipts || []);
    } catch (ex) {
      setErr(errorMessage(ex, "Could not load the money on account receipts"));
      setLinking(null);
    }
  };

  const link = async (quoteId, moaId) => {
    const ok = await act(() => axios.post(`${API}/matters/${enc}/disbursement-quotes/${quoteId}/link-receipt`,
      { moa_entry_id: moaId }), "Could not link the receipt");
    if (ok) setLinking(null);
  };

  const withdraw = async (quoteId) => {
    if (!reason.trim()) return setErr("Say why the quote is withdrawn");
    const ok = await act(() => axios.post(`${API}/matters/${enc}/disbursement-quotes/${quoteId}/withdraw`,
      { reason: reason.trim() }), "Could not withdraw the quote");
    if (ok) { setWithdrawing(null); setReason(""); }
  };

  if (loadErr) return <p className="mt-3 text-xs text-status-red" data-testid="disb-quotes-error">{loadErr}</p>;
  if (quotes == null) return null;

  const shown = quotes.filter((q) => q.status !== "withdrawn");
  const awaiting = shown.filter((q) => q.status === "awaiting_moa");

  return (
    <div className="mt-3" data-testid="disb-quotes-card">
      {awaiting.length > 0 && (
        <p className="mb-1.5 flex items-center gap-1.5 text-xs text-status-amber" data-testid="disb-quotes-flag">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          {awaiting.length === 1 ? "A disbursement quote is" : `${awaiting.length} disbursement quotes are`} awaiting
          money on account. It cannot be paid until accounts links the client's money to it.
        </p>
      )}
      {shown.map((q) => (
        <div key={q.id} className="mb-1.5 rounded-md border border-white/10 bg-slate-900/40 px-2.5 py-1.5 text-xs" data-testid="disb-quote-row">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-slate-200 min-w-0 truncate" title={q.description}>
              <Receipt className="inline w-3.5 h-3.5 mr-1 text-indigo-300" />
              <span className="text-white">{q.supplier}</span>: {q.description}
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap">
              <span className="text-txt-muted">{fmt(q.gross_amount)}{q.vat_treatment === "plus_vat" ? " inc VAT" : ""}</span>
              {q.status === "covered" ? (
                <span className="inline-flex items-center gap-1 text-status-green" data-testid="disb-quote-covered">
                  <CheckCircle2 className="w-3.5 h-3.5" /> MOA in
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-status-amber" data-testid="disb-quote-awaiting">
                  <AlertTriangle className="w-3.5 h-3.5" /> Awaiting MOA{q.received > 0 ? ` (${fmt(q.outstanding)} to go)` : ""}
                </span>
              )}
              {q.status === "awaiting_moa" && linker && (
                <button onClick={() => openLink(q.id)} title="Link money on account received"
                  className="inline-flex items-center gap-1 text-indigo-300 hover:text-indigo-200" data-testid="disb-quote-link">
                  <Link2 className="w-3.5 h-3.5" /> Link receipt
                </button>
              )}
              {q.status === "awaiting_moa" && !(q.allocations || []).length && (
                <button onClick={() => { setWithdrawing(q.id); setReason(""); setErr(""); }} title="Withdraw (supplier not instructed)"
                  className="opacity-60 hover:opacity-100 text-slate-300" data-testid="disb-quote-withdraw">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          </div>
          <p className="text-[10px] text-txt-muted mt-0.5">Quoted {q.quoted_on}</p>
          {linking === q.id && (
            <div className="mt-1.5 border-t border-white/10 pt-1.5" data-testid="disb-quote-receipts">
              {receipts == null ? <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" /> :
                receipts.filter((r) => r.unlinked > 0).length === 0 ? (
                  <p className="text-txt-muted">No money on account receipt on this matter has anything left to link. Record the receipt first.</p>
                ) : receipts.filter((r) => r.unlinked > 0).map((r) => (
                  <div key={r.id} className="flex items-center justify-between gap-2 py-0.5">
                    <span className="text-slate-300">{r.date_received}: {fmt(r.amount)}{r.unlinked < r.amount ? ` (${fmt(r.unlinked)} unlinked)` : ""}{r.notes ? `, ${r.notes}` : ""}</span>
                    <button disabled={saving} onClick={() => link(q.id, r.id)}
                      className="px-2 py-0.5 rounded bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60">Link</button>
                  </div>
                ))}
              <button onClick={() => setLinking(null)} className="mt-1 text-txt-muted hover:text-white">Cancel</button>
            </div>
          )}
          {withdrawing === q.id && (
            <div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-white/10 pt-1.5">
              <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why, e.g. counsel not instructed"
                className={`${inputCls} flex-1 min-w-[180px]`} data-testid="disb-quote-withdraw-reason" />
              <button disabled={saving} onClick={() => withdraw(q.id)}
                className="px-2 py-1 rounded bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60">Withdraw</button>
              <button onClick={() => setWithdrawing(null)} className="text-txt-muted hover:text-white">Cancel</button>
            </div>
          )}
        </div>
      ))}
      {!adding ? (
        <button onClick={() => { setAdding(true); setErr(""); setForm({ supplier: "", description: "", amount: "", vat_treatment: "", quoted_on: todayIso() }); }}
          className="inline-flex items-center gap-1.5 text-xs text-indigo-300 hover:text-indigo-200" data-testid="disb-quote-add">
          <Plus className="w-3.5 h-3.5" /> Add a disbursement quote (e.g. counsel's fee)
        </button>
      ) : (
        <form onSubmit={save} className="mt-1 flex flex-wrap items-end gap-2 text-xs" data-testid="disb-quote-form">
          <label className="flex flex-col gap-1">
            <span className="text-txt-muted">Who quoted</span>
            <input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} maxLength={200}
              placeholder="e.g. counsel's name" className={`${inputCls} w-40`} data-testid="disb-quote-supplier" autoFocus />
          </label>
          <label className="flex flex-col gap-1 flex-1 min-w-[180px]">
            <span className="text-txt-muted">What for</span>
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={500}
              placeholder="e.g. Advice on merits" className={inputCls} data-testid="disb-quote-description" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-txt-muted">Amount (£)</span>
            <input value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} inputMode="decimal"
              className={`${inputCls} w-24`} data-testid="disb-quote-amount" />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-txt-muted">VAT</span>
            <select value={form.vat_treatment} onChange={(e) => setForm({ ...form, vat_treatment: e.target.value })}
              className={inputCls} data-testid="disb-quote-vat">
              <option value="">Choose</option>
              {VAT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-txt-muted">Quoted on</span>
            <input type="date" value={form.quoted_on} max={todayIso()} onChange={(e) => setForm({ ...form, quoted_on: e.target.value })}
              className={inputCls} data-testid="disb-quote-date" />
          </label>
          <button type="submit" disabled={saving}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white disabled:opacity-60"
            data-testid="disb-quote-save">
            {saving && <Loader2 className="w-3 h-3 animate-spin" />} Save
          </button>
          <button type="button" onClick={() => setAdding(false)}
            className="px-3 py-1.5 rounded-md border border-slate-600 text-slate-300 hover:bg-slate-800">Cancel</button>
        </form>
      )}
      {err && <p className="mt-1 text-xs text-status-red" data-testid="disb-quote-err">{err}</p>}
    </div>
  );
}
